// SNIGJ - Register Page

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Scale, Mail, Lock, User, Sun, Moon, Globe, AlertCircle, Eye, EyeOff, Building2, Phone, MapPin, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'CITIZEN',
    language: 'fr',
    national_id: '',
    phone_number: '',
    // Person type for citizens
    person_type: 'PHYSICAL', // PHYSICAL or LEGAL
    // Legal entity fields
    company_name: '',
    company_registration: '', // Numéro RCCM
    company_tax_id: '', // Numéro impôt
    legal_representative: '',
    company_address: ''
  });

  // Load OAuth config
  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        const response = await api.get('/config/oauth');
        if (response.data?.google_client_id) {
          setGoogleClientId(response.data.google_client_id);
        }
      } catch (error) {
        console.log('OAuth config not available');
      }
    };
    loadOAuthConfig();
  }, []);

  // Handle Google registration
  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const response = await api.post('/auth/google-register', {
        credential: credentialResponse.credential,
        person_type: formData.person_type,
        company_name: formData.company_name,
        company_registration: formData.company_registration,
        company_tax_id: formData.company_tax_id,
        legal_representative: formData.legal_representative,
        company_address: formData.company_address
      });
      
      // Store token and redirect - account is auto-verified
      localStorage.setItem('token', response.data.access_token);
      toast.success(language === 'fr' ? 'Compte créé et vérifié avec succès !' : 'Account created and verified!');
      navigate('/citizen-portal');
    } catch (err) {
      if (err.response?.status === 409) {
        // Account already exists, redirect to login
        toast.info(language === 'fr' ? 'Ce compte existe déjà. Connectez-vous.' : 'Account already exists. Please login.');
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || (language === 'fr' ? "Erreur d'inscription Google" : 'Google registration failed'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(language === 'fr' ? 'Erreur de connexion Google' : 'Google connection error');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      // Add flag to indicate manual registration (needs email verification)
      userData.requires_verification = true;
      
      await api.post('/auth/register', userData);
      
      // Show verification pending message
      setPendingVerification(true);
      setRegistrationComplete(true);
      toast.success(language === 'fr' ? 'Un email de vérification a été envoyé' : 'Verification email sent');
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' ? "Erreur d'inscription" : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  // Only show basic roles for public registration
  const publicRoles = ['CITIZEN'];
  
  // Officials roles (require admin approval)
  const officialRoles = [
    'POLICE_OFFICER',
    'JUDICIAL_POLICE_OFFICER',
    'MAGISTRATE',
    'CLERK',
    'PRISON_OFFICER',
    'BORDER_CONTROL_OFFICER'
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      {/* Top bar */}
      <div className="flex justify-end items-center gap-3 p-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => updateLanguage(language === 'fr' ? 'en' : 'fr')}
          data-testid="register-lang-toggle"
        >
          <Globe className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'EN' : 'FR'}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="register-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      {/* Form container */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded bg-yellow-400 flex items-center justify-center">
                <Scale className="w-8 h-8 text-slate-900" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">{t(language, 'auth.register')}</CardTitle>
            <CardDescription>{t(language, 'common.ministry')}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Verification Pending Message */}
            {pendingVerification ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {language === 'fr' ? 'Vérifiez votre email' : 'Check your email'}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {language === 'fr' 
                    ? `Un email de vérification a été envoyé à ${formData.email}. Cliquez sur le lien pour activer votre compte.`
                    : `A verification email has been sent to ${formData.email}. Click the link to activate your account.`}
                </p>
                <Button variant="outline" onClick={() => navigate('/login')}>
                  {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
                </Button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {/* Social Login Buttons - Always show */}
                <div className="mb-6">
                  <p className="text-center text-sm text-muted-foreground mb-3">
                    {language === 'fr' ? 'Inscription rapide et vérifiée' : 'Quick and verified registration'}
                  </p>
                  <div className="flex flex-col gap-3">
                    {/* Google Button */}
                    {googleClientId ? (
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <div className="flex justify-center">
                          <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleError}
                            theme={theme === 'dark' ? 'filled_black' : 'outline'}
                            size="large"
                            text="signup_with"
                            width="100%"
                            logo_alignment="center"
                          />
                        </div>
                      </GoogleOAuthProvider>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 h-10"
                        onClick={() => toast.info(language === 'fr' ? 'Connexion Google en cours de configuration' : 'Google Sign-In being configured')}
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        {language === 'fr' ? "S'inscrire avec Google" : 'Sign up with Google'}
                      </Button>
                    )}
                    
                    {/* Apple Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full flex items-center justify-center gap-2 h-10"
                      onClick={() => toast.info(language === 'fr' ? 'Apple Sign-In bientôt disponible' : 'Apple Sign-In coming soon')}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      {language === 'fr' ? "S'inscrire avec Apple" : 'Sign up with Apple'}
                    </Button>
                  </div>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-card text-muted-foreground">
                        {language === 'fr' ? 'ou avec email' : 'or with email'}
                      </span>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t(language, 'auth.name')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="Jean Mutombo"
                        className="pl-10"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="register-name"
                      />
                    </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t(language, 'auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="nom@justice.gouv.cd"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">{t(language, 'admin.role')}</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="register-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CITIZEN">
                      {t(language, 'roles.CITIZEN')} ({language === 'fr' ? 'Compte personnel' : 'Personal account'})
                    </SelectItem>
                    <SelectItem disabled value="divider">
                      ── {language === 'fr' ? 'Agents (nécessite approbation)' : 'Officials (requires approval)'} ──
                    </SelectItem>
                    {officialRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {t(language, `roles.${role}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.role === 'CITIZEN' && (
                <>
                  {/* Person Type Selection */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Type de compte' : 'Account Type'}</Label>
                    <Select
                      value={formData.person_type}
                      onValueChange={(value) => setFormData({ ...formData, person_type: value })}
                    >
                      <SelectTrigger data-testid="register-person-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PHYSICAL">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {language === 'fr' ? 'Personne Physique' : 'Individual'}
                          </div>
                        </SelectItem>
                        <SelectItem value="LEGAL">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {language === 'fr' ? 'Personne Morale (Entreprise/Organisation)' : 'Legal Entity (Company/Organization)'}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Physical Person Fields */}
                  {formData.person_type === 'PHYSICAL' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="national_id">{language === 'fr' ? "Numéro de carte d'identité (optionnel)" : 'National ID (optional)'}</Label>
                        <Input
                          id="national_id"
                          type="text"
                          placeholder="XXX-XXXX-XXXX"
                          value={formData.national_id}
                          onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                          data-testid="register-national-id"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">{language === 'fr' ? 'Numéro de téléphone (optionnel)' : 'Phone number (optional)'}</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="+243 XXX XXX XXX"
                            className="pl-10"
                            value={formData.phone_number}
                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            data-testid="register-phone"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Legal Entity Fields */}
                  {formData.person_type === 'LEGAL' && (
                    <>
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          {language === 'fr' 
                            ? "Pour les entreprises et organisations. Les informations seront vérifiées avant validation du compte."
                            : "For companies and organizations. Information will be verified before account validation."}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="company_name">{language === 'fr' ? "Raison sociale *" : 'Company Name *'}</Label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="company_name"
                            type="text"
                            placeholder={language === 'fr' ? "Nom de l'entreprise" : "Company name"}
                            className="pl-10"
                            value={formData.company_name}
                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                            required={formData.person_type === 'LEGAL'}
                            data-testid="register-company-name"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="company_registration">{language === 'fr' ? 'N° RCCM *' : 'Registration No. *'}</Label>
                          <Input
                            id="company_registration"
                            type="text"
                            placeholder="CD/KIN/RCCM/XXX"
                            value={formData.company_registration}
                            onChange={(e) => setFormData({ ...formData, company_registration: e.target.value })}
                            required={formData.person_type === 'LEGAL'}
                            data-testid="register-rccm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="company_tax_id">{language === 'fr' ? "N° Impôt" : 'Tax ID'}</Label>
                          <Input
                            id="company_tax_id"
                            type="text"
                            placeholder="A0XXXXXX"
                            value={formData.company_tax_id}
                            onChange={(e) => setFormData({ ...formData, company_tax_id: e.target.value })}
                            data-testid="register-tax-id"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="legal_representative">{language === 'fr' ? 'Représentant légal *' : 'Legal Representative *'}</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="legal_representative"
                            type="text"
                            placeholder={language === 'fr' ? "Nom du gérant/directeur" : "Manager/Director name"}
                            className="pl-10"
                            value={formData.legal_representative}
                            onChange={(e) => setFormData({ ...formData, legal_representative: e.target.value })}
                            required={formData.person_type === 'LEGAL'}
                            data-testid="register-representative"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company_address">{language === 'fr' ? 'Siège social' : 'Headquarters Address'}</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="company_address"
                            type="text"
                            placeholder={language === 'fr' ? "Adresse du siège" : "Headquarters address"}
                            className="pl-10"
                            value={formData.company_address}
                            onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                            data-testid="register-company-address"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">{language === 'fr' ? 'Téléphone entreprise' : 'Company Phone'}</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="+243 XXX XXX XXX"
                            className="pl-10"
                            value={formData.phone_number}
                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            data-testid="register-company-phone"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">{t(language, 'auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t(language, 'auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Info about email verification */}
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {language === 'fr' 
                    ? "Un email de vérification sera envoyé pour activer votre compte."
                    : "A verification email will be sent to activate your account."}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? t(language, 'common.loading') : t(language, 'auth.registerButton')}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              {t(language, 'auth.hasAccount')}{' '}
              <Link to="/login" className="text-yellow-600 hover:underline font-medium" data-testid="login-link">
                {t(language, 'auth.loginButton')}
              </Link>
            </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="p-4 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {t(language, 'common.ministry')} - {t(language, 'common.republic')}
      </div>
    </div>
  );
};

export default RegisterPage;
